"use client";

import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "../UI/Button/Button";
import Card from "../UI/Card/Card";
import Tabs from "../UI/Tabs/Tabs";
import Alert from "../UI/Alert/Alert";
import Badge from "../UI/Badge/Badge";
import {
  getMappedPatientData,
} from "../../services/data-service";
import Loader from "../UI/Loader/Loader";
import "./ReviewPage.css";

function ReviewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const uhid = searchParams.get("uhid");

  const [loading, setLoading] = useState(true);
  const [patientData, setPatientData] = useState(null);
  const [mappingPaths, setMappingPaths] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  useEffect(() => {
    async function loadData() {
      if (!uhid) {
        setError("UHID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const { mappedData, mappingPaths } = await getMappedPatientData(uhid);
        setPatientData(mappedData);
        setMappingPaths(mappingPaths);
      } catch (err) {
        console.error("Data fetch error:", err);
        setError("Error fetching mapped patient data");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [uhid]);

  const handleSubmit = async () => {
    if (!patientData) return;
    setSubmitting(true);
    setError(null);
    navigate(`/results?uhid=${uhid}`);
  };

  if (loading) {
    return (
      <div className="review-container">
        <div className="loading-wrapper">
          <Card className="loading-card">
            <div className="loading-content">
              <Loader className="loader-animation" />
              <p className="loading-text">Loading patient data...</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="review-container">
        <div className="error-wrapper slide-up">
          <Card className="error-card">
            <div className="error-content">
              <Alert variant="destructive" title="Error" description={error} />
              <div className="error-actions">
                <Button onClick={() => navigate("/home")}>
                  Return to Home
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="review-container">
      <div className="review-content slide-up">
        <Card className="review-card">
          <div className="card-header">
            <h2 className="card-title">Review Mapped Patient Data</h2>
            <p className="card-description">
              Review the extracted data before submitting to the AICVD risk
              assessment model
            </p>
          </div>
          <div className="card-content">
            {patientData && (
              <Tabs
                tabs={[
                  { id: "basic", label: "Basic Information" },
                  { id: "medical", label: "Medical Parameters" },
                  { id: "lifestyle", label: "Lifestyle Factors" },
                ]}
                activeTab={activeTab}
                onChange={setActiveTab}
              >
                {activeTab === "basic" && (
                  <div className="data-grid fade-in">
                    <div className="data-item">
                      <p className="data-label">Patient ID</p>
                      <p className="data-value">{patientData.Id || "N/A"}</p>
                    </div>
                    <div className="data-item">
                      <p className="data-label">AHC Number</p>
                      <p className="data-value">{patientData.AHCno || "N/A"}</p>
                    </div>
                    <div className="data-item">
                      <p className="data-label">Name</p>
                      <p className="data-value">{patientData.Name || "N/A"}</p>
                    </div>
                    <div className="data-item">
                      <p className="data-label">Age</p>
                      <p className="data-value">{patientData.Age || "N/A"}</p>
                    </div>
                    <div className="data-item">
                      <p className="data-label">Gender</p>
                      <p className="data-value">
                        {patientData.Gender || "N/A"}
                      </p>
                    </div>
                    <div className="data-item">
                      <p className="data-label">BMI</p>
                      <p className="data-value">{patientData.BMI || "N/A"}</p>
                    </div>
                    <div className="data-item">
                      <p className="data-label">Height</p>
                      <p className="data-value">
                        {patientData.Height || "N/A"}
                      </p>
                    </div>
                    <div className="data-item">
                      <p className="data-label">Weight</p>
                      <p className="data-value">
                        {patientData.Weight || "N/A"}
                      </p>
                    </div>
                  </div>
                )}

                {activeTab === "medical" && (
                  <div className="data-grid fade-in">
                    <div className="data-item">
                      <p className="data-label">Blood Pressure (Systolic)</p>
                      <p className="data-value">
                        {patientData.BloodPressureSystolic || "N/A"} mmHg
                      </p>
                    </div>
                    <div className="data-item">
                      <p className="data-label">Blood Pressure (Diastolic)</p>
                      <p className="data-value">
                        {patientData.BloodPressureDiastolic || "N/A"} mmHg
                      </p>
                    </div>
                    <div className="data-item">
                      <p className="data-label">Heart Rate</p>
                      <p className="data-value">
                        {patientData.HeartRatePerMinute || "N/A"} bpm
                      </p>
                    </div>
                    <div className="data-item">
                      <p className="data-label">Diabetes Mellitus</p>
                      <div className="data-value-with-badge">
                        <p>{patientData.DiabetesMellitus || "N/A"}</p>
                        {mappingPaths && mappingPaths.DiabetesMellitus && (
                          <Badge>
                            From: {mappingPaths.DiabetesMellitus.split(">")[0]}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="data-item">
                      <p className="data-label">Hypertension</p>
                      <div className="data-value-with-badge">
                        <p>{patientData.Hypertension || "N/A"}</p>
                        {mappingPaths && mappingPaths.Hypertension && (
                          <Badge>
                            From: {mappingPaths.Hypertension.split(">")[0]}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="data-item">
                      <p className="data-label">Dyslipidaemia</p>
                      <div className="data-value-with-badge">
                        <p>{patientData.Dyslipidaemia || "N/A"}</p>
                        {mappingPaths && mappingPaths.Dyslipidaemia && (
                          <Badge>
                            From: {mappingPaths.Dyslipidaemia.split(">")[0]}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "lifestyle" && (
                  <div className="data-grid fade-in">
                    <div className="data-item">
                      <p className="data-label">Physical Activity</p>
                      <p className="data-value">
                        {patientData.PhysicalActivity || "N/A"}
                      </p>
                    </div>
                    <div className="data-item">
                      <p className="data-label">Smoking</p>
                      <p className="data-value">{patientData.Smoke || "N/A"}</p>
                    </div>
                    <div className="data-item">
                      <p className="data-label">Tobacco</p>
                      <p className="data-value">
                        {patientData.Tobacco || "N/A"}
                      </p>
                    </div>
                    <div className="data-item">
                      <p className="data-label">Diet</p>
                      <p className="data-value">{patientData.Diet || "N/A"}</p>
                    </div>
                    <div className="data-item">
                      <p className="data-label">Alcohol</p>
                      <p className="data-value">
                        {patientData.Alcohol || "N/A"}
                      </p>
                    </div>
                  </div>
                )}
              </Tabs>
            )}

            {error && (
              <div className="submission-error">
                <Alert
                  variant="destructive"
                  title="Submission Error"
                  description={error}
                />
              </div>
            )}
          </div>
          <div className="card-footer">
            <div className="footer-actions">
              <Button
                variant="outline"
                onClick={() => navigate(`/mapping?uhid=${uhid}`)}
              >
                Back to Mapping
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="submit-button"
              >
                {submitting ? (
                  <div className="submitting-button">
                    <Loader size="small" />
                    <span>Submitting...</span>
                  </div>
                ) : (
                  "Submit to AICVD Model"
                )}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default ReviewPage;
